import React from 'react';
import clsx from 'clsx';
import {Alert} from '@netx/core-components/Alert';
import {ClipboardTextField} from '@netx/core-components/ClipboardTextField';
import {ScrollBorder} from '@netx/core-components/ScrollBorder';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {CUSTOM_ACTION_LABEL, CUSTOM_ACTION_MESSAGE} from '../../locales';
import {CustomActionField} from '../../schema';
import type {CustomAction} from '../../types';
import {
	CustomActionEndpointPayloadUrl,
	CustomActionEndpointTargetUrl,
} from '../CustomActionEndpoint';
import {
	CUSTOM_ACTION_CREATED_CLASS_NAME,
	customActionCreatedClasses,
	CustomActionCreatedClasses,
	getCustomActionCreatedUtilityClass,
} from './customActionCreatedClasses';

export type CustomActionCreatedOwnerState = WithClasses<
	CustomActionCreatedClasses,
	{}
>;

const useUtilityClasses = (ownerState: CustomActionCreatedOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCustomActionCreatedUtilityClass, classes);
};

const CustomActionCreatedRoot = styled('div', {
	name: CUSTOM_ACTION_CREATED_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
}));

const CustomActionCreatedActions = styled('div')(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(1, 2),
	marginTop: theme.spacing(2),
	borderRadius: 8,
	border: `1px solid ${theme.palette.custom.border}`,
}));

const CustomActionCreatedAlert = styled(Alert)(({theme}) => ({
	margin: theme.spacing(0, 2, 2, 2),
}));

const CustomActionCreatedInputWrapper = styled('div')(({theme}) => ({
	marginTop: theme.spacing(1),
	marginBottom: theme.spacing(1),
}));

export type CustomActionCreatedProps = WithClasses<
	CustomActionCreatedClasses,
	{
		customAction: Required<CustomAction>;
	}
>;

export const CustomActionCreatedPrimitive = ({
	classes: _classes,
	className,
	customAction,
}: CustomActionCreatedProps) => {
	const ownerState: CustomActionCreatedOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<CustomActionCreatedRoot className={clsx(classes.root, className)}>
			<CustomActionCreatedAlert severity="warning">
				{CUSTOM_ACTION_MESSAGE.SECRETE_MESSAGE}
			</CustomActionCreatedAlert>

			<ScrollBorder contentPadding={'8px 16px 0 16px'}>
				<ClipboardTextField
					fullWidth
					label={CUSTOM_ACTION_LABEL.SECRET}
					name="secret"
					value={customAction.secret}
				/>

				<CustomActionCreatedActions>
					<CustomActionCreatedInputWrapper>
						<CustomActionEndpointPayloadUrl
							fullWidth
							value={customAction[CustomActionField.payloadUrl]}
							customActionId={customAction.id}
							readOnly
						/>
					</CustomActionCreatedInputWrapper>

					<CustomActionCreatedInputWrapper>
						<CustomActionEndpointTargetUrl
							fullWidth
							value={customAction[CustomActionField.targetUrl]}
							customActionId={customAction.id}
							readOnly
						/>
					</CustomActionCreatedInputWrapper>
				</CustomActionCreatedActions>
			</ScrollBorder>
		</CustomActionCreatedRoot>
	);
};

export const CustomActionCreated = CustomActionCreatedPrimitive;

export default CustomActionCreated;
